'use client';

import React from 'react';
import { useTranslation } from '@/hooks/useTranslation';

// IMPORTANT: Replace ALL bracketed placeholders like [Your Company Name],
// [Date], [Contact Email], etc., with your actual information.
// This is template content and requires review by a legal professional.

export default function PrivacyPolicyClient() {
    const { t } = useTranslation();
    const lastUpdatedDate = t('privacy.lastUpdatedDate', {
        fallback: 'April 16, 2025',
    });

    return (
        <div className="container mx-auto max-w-3xl px-4 py-12 md:py-16">
            <h1 className="text-3xl font-bold mb-4 text-foreground">
                {t('privacy.title', { fallback: 'Privacy Policy' })}
            </h1>
            <p className="text-sm text-muted-foreground mb-8">
                {t('privacy.lastUpdatedPrefix', { fallback: 'Last updated:' })}{' '}
                {lastUpdatedDate}
            </p>

            <div className="prose dark:prose-invert max-w-none space-y-6">
                {/* Introduction */}
                <section className="mb-8">
                    <h2 className="text-2xl font-semibold mb-4">
                        {t('privacy.introduction.title', {
                            fallback: '1. Introduction',
                        })}
                    </h2>
                    <p className="mb-4">
                        {t('privacy.introduction.p1', {
                            fallback:
                                'Welcome to URLPrompter ("we", "us", "our"). We are committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our website and services (collectively, the "Service"). Please read this privacy policy carefully.',
                        })}
                    </p>
                    <p>
                        {t('privacy.introduction.p2', {
                            fallback:
                                'By using the Service, you agree to the collection and use of information in accordance with this policy.',
                        })}
                    </p>
                </section>

                {/* Information We Collect */}
                <section className="mb-8">
                    <h2 className="text-2xl font-semibold mb-4">
                        {t('privacy.informationCollected.title', {
                            fallback: '2. Information We Collect',
                        })}
                    </h2>
                    <p className="mb-4">
                        {t('privacy.informationCollected.p1', {
                            fallback:
                                'We may collect several different types of information for various purposes to provide and improve our Service to you.',
                        })}
                    </p>
                    <h3 className="text-xl font-medium mb-3 mt-6">
                        {t('privacy.informationCollected.personalData.title', {
                            fallback: 'Personal Data',
                        })}
                    </h3>
                    <p className="mb-4">
                        {t('privacy.informationCollected.personalData.p1', {
                            fallback:
                                'While using our Service, we may ask you to provide us with certain personally identifiable information ("Personal Data"). This may include, but is not limited to:',
                        })}
                    </p>
                    <ul className="list-disc pl-6 mb-4 space-y-1">
                        <li>
                            {t(
                                'privacy.informationCollected.personalData.item1',
                                { fallback: 'Email address' }
                            )}
                        </li>
                        <li>
                            {t(
                                'privacy.informationCollected.personalData.item2',
                                {
                                    fallback:
                                        'First name and last name (Optional)',
                                }
                            )}
                        </li>
                        <li>
                            {t(
                                'privacy.informationCollected.personalData.item3',
                                {
                                    fallback:
                                        'Payment information (handled by Stripe, see below)',
                                }
                            )}
                        </li>
                        <li>
                            {t(
                                'privacy.informationCollected.personalData.item4',
                                {
                                    fallback:
                                        'Contact form submissions (Name, Email, Message)',
                                }
                            )}
                        </li>
                    </ul>
                    <h3 className="text-xl font-medium mb-3 mt-6">
                        {t('privacy.informationCollected.usageData.title', {
                            fallback: 'Usage Data',
                        })}
                    </h3>
                    <p className="mb-4">
                        {t('privacy.informationCollected.usageData.p1', {
                            fallback:
                                'We may also collect information on how the Service is accessed and used ("Usage Data"). This Usage Data may include information such as your computer\'s Internet Protocol address (e.g., IP address), browser type, browser version, the pages of our Service that you visit, the time and date of your visit, the time spent on those pages, unique device identifiers, and other diagnostic data.',
                        })}
                    </p>
                    <h3 className="text-xl font-medium mb-3 mt-6">
                        {t('privacy.informationCollected.contentData.title', {
                            fallback: 'Content Data',
                        })}
                    </h3>
                    <p className="mb-4">
                        {t('privacy.informationCollected.contentData.p1', {
                            fallback:
                                'When you submit a URL and a prompt to our Service for processing, we necessarily handle the URL itself and the prompt text you provide. We may temporarily process the content fetched from the provided URL to generate the requested output.',
                        })}
                    </p>
                </section>

                {/* How We Use Your Information */}
                <section className="mb-8">
                    <h2 className="text-2xl font-semibold mb-4">
                        {t('privacy.howWeUse.title', {
                            fallback: '3. How We Use Your Information',
                        })}
                    </h2>
                    <p className="mb-4">
                        {t('privacy.howWeUse.p1', {
                            fallback:
                                'We use the collected data for various purposes:',
                        })}
                    </p>
                    <ul className="list-disc pl-6 mb-4 space-y-1">
                        <li>
                            {t('privacy.howWeUse.item1', {
                                fallback: 'To provide and maintain our Service',
                            })}
                        </li>
                        <li>
                            {t('privacy.howWeUse.item2', {
                                fallback:
                                    'To manage your account and subscriptions',
                            })}
                        </li>
                        <li>
                            {t('privacy.howWeUse.item3', {
                                fallback:
                                    'To process your payments (via Stripe)',
                            })}
                        </li>
                        <li>
                            {t('privacy.howWeUse.item4', {
                                fallback:
                                    'To provide customer support and respond to inquiries',
                            })}
                        </li>
                        <li>
                            {t('privacy.howWeUse.item5', {
                                fallback: 'To monitor the usage of our Service',
                            })}
                        </li>
                        <li>
                            {t('privacy.howWeUse.item6', {
                                fallback:
                                    'To detect, prevent and address technical issues',
                            })}
                        </li>
                        <li>
                            {t('privacy.howWeUse.item7', {
                                fallback:
                                    'To improve the Service, including the underlying AI models',
                            })}
                        </li>
                    </ul>
                </section>

                {/* Information Sharing and Disclosure */}
                <section className="mb-8">
                    <h2 className="text-2xl font-semibold mb-4">
                        {t('privacy.sharingDisclosure.title', {
                            fallback: '4. Information Sharing and Disclosure',
                        })}
                    </h2>
                    <h3 className="text-xl font-medium mb-3 mt-6">
                        {t('privacy.sharingDisclosure.serviceProviders.title', {
                            fallback: 'Service Providers',
                        })}
                    </h3>
                    <p className="mb-4">
                        {t('privacy.sharingDisclosure.serviceProviders.p1', {
                            fallback:
                                'We employ third-party companies and individuals to facilitate our Service ("Service Providers"), provide the Service on our behalf, perform Service-related services, or assist us in analyzing how our Service is used. These third parties have access to your Personal Data only to perform these tasks on our behalf and are obligated not to disclose or use it for any other purpose.',
                        })}
                    </p>
                    <ul className="list-disc pl-6 mb-4 space-y-1">
                        <li>
                            {t(
                                'privacy.sharingDisclosure.serviceProviders.item1',
                                {
                                    fallback:
                                        'Firebase (Google Cloud): For authentication, database, cloud functions, and hosting.',
                                }
                            )}
                        </li>
                        <li>
                            {t(
                                'privacy.sharingDisclosure.serviceProviders.item2',
                                {
                                    fallback:
                                        'Stripe: For payment processing. We do not store your full credit card details.',
                                }
                            )}
                        </li>
                        <li>
                            {t(
                                'privacy.sharingDisclosure.serviceProviders.item3',
                                {
                                    fallback:
                                        'Telegram: For sending contact form notifications to us.',
                                }
                            )}
                        </li>
                    </ul>

                    {/* AI Processing section */}
                    <h3 className="text-xl font-medium mb-3 mt-6">
                        {t('privacy.sharingDisclosure.aiProcessing.title', {
                            fallback: 'AI Processing (xAI)',
                        })}
                    </h3>
                    <p className="mb-4">
                        {t('privacy.sharingDisclosure.aiProcessing.p1', {
                            fallback:
                                'Our core content processing functionality is powered by large language models from xAI. When you submit a URL and prompt, this data (including potentially the content fetched from the URL) is sent to xAI APIs to generate the requested summary, analysis, or other output. Your use of the Service constitutes your consent to this processing by xAI.',
                        })}
                    </p>
                    <p className="mb-4">
                        {t('privacy.sharingDisclosure.aiProcessing.p2', {
                            fallback:
                                "We encourage you to review xAI's own privacy policies and terms. We endeavor to configure our usage of these services in a way that respects user privacy.",
                        })}
                    </p>

                    {/* Legal Requirements section */}
                    <h3 className="text-xl font-medium mb-3 mt-6">
                        {t('privacy.sharingDisclosure.legal.title', {
                            fallback: 'Legal Requirements',
                        })}
                    </h3>
                    <p className="mb-4">
                        {t('privacy.sharingDisclosure.legal.p1', {
                            fallback:
                                'URLPrompter may disclose your Personal Data in the good faith belief that such action is necessary to:',
                        })}
                    </p>
                    <ul className="list-disc pl-6 mb-4 space-y-1">
                        <li>
                            {t('privacy.sharingDisclosure.legal.item1', {
                                fallback: 'To comply with a legal obligation',
                            })}
                        </li>
                        <li>
                            {t('privacy.sharingDisclosure.legal.item2', {
                                fallback:
                                    'To protect and defend the rights or property of URLPrompter',
                            })}
                        </li>
                        <li>
                            {t('privacy.sharingDisclosure.legal.item3', {
                                fallback:
                                    'To prevent or investigate possible wrongdoing in connection with the Service',
                            })}
                        </li>
                        <li>
                            {t('privacy.sharingDisclosure.legal.item4', {
                                fallback:
                                    'To protect the personal safety of users of the Service or the public',
                            })}
                        </li>
                        <li>
                            {t('privacy.sharingDisclosure.legal.item5', {
                                fallback: 'To protect against legal liability',
                            })}
                        </li>
                    </ul>
                </section>

                {/* Data Security */}
                <section className="mb-8">
                    <h2 className="text-2xl font-semibold mb-4">
                        {t('privacy.dataSecurity.title', {
                            fallback: '5. Data Security',
                        })}
                    </h2>
                    <p className="mb-4">
                        {t('privacy.dataSecurity.p1', {
                            fallback:
                                'The security of your data is important to us, but remember that no method of transmission over the Internet or method of electronic storage is 100% secure. While we strive to use commercially acceptable means to protect your Personal Data (e.g., encryption, secure cloud infrastructure), we cannot guarantee its absolute security.',
                        })}
                    </p>
                    <p className="mb-4">
                        {t('privacy.dataSecurity.p2', {
                            fallback:
                                'We implement industry-standard security measures including HTTPS encryption, secure password hashing, and regular security audits.',
                        })}
                    </p>
                </section>

                {/* Data Retention */}
                <section className="mb-8">
                    <h2 className="text-2xl font-semibold mb-4">
                        {t('privacy.dataRetention.title', {
                            fallback: '6. Data Retention',
                        })}
                    </h2>
                    <p className="mb-4">
                        {t('privacy.dataRetention.p1', {
                            fallback:
                                'We will retain your Personal Data only for as long as is necessary for the purposes set out in this Privacy Policy. We will retain and use your Personal Data to the extent necessary to comply with our legal obligations (for example, if we are required to retain your data to comply with applicable laws), resolve disputes, and enforce our legal agreements and policies.',
                        })}
                    </p>
                    <p className="mb-4">
                        {t('privacy.dataRetention.p2', {
                            fallback:
                                'Usage Data is generally retained for a shorter period, except when this data is used to strengthen the security or to improve the functionality of our Service, or we are legally obligated to retain this data for longer periods.',
                        })}
                    </p>
                    <p className="mb-4">
                        {t('privacy.dataRetention.p3', {
                            fallback:
                                'Content processed via the API is typically retained for 30 days for debugging and analysis purposes and then deleted.',
                        })}
                    </p>
                </section>

                {/* Your Data Protection Rights */}
                <section className="mb-8">
                    <h2 className="text-2xl font-semibold mb-4">
                        {t('privacy.yourRights.title', {
                            fallback: '7. Your Data Protection Rights',
                        })}
                    </h2>
                    <p className="mb-4">
                        {t('privacy.yourRights.p1', {
                            fallback:
                                'Depending on your location, you may have certain data protection rights.',
                        })}
                    </p>
                    <p className="mb-4">
                        {t('privacy.yourRights.gdpr.p1', {
                            fallback:
                                'If you are a resident of the European Economic Area (EEA), you have certain data protection rights. We aim to take reasonable steps to allow you to correct, amend, delete, or limit the use of your Personal Data.',
                        })}
                    </p>
                    <p className="mb-4">
                        {t('privacy.yourRights.gdpr.p2', {
                            fallback:
                                'If you wish to be informed about what Personal Data we hold about you and if you want it to be removed from our systems, please contact us at support@urlprompter.com.',
                        })}
                    </p>
                    <p className="mb-4">
                        {t('privacy.yourRights.gdpr.p3', {
                            fallback:
                                'In certain circumstances, you have the following data protection rights:',
                        })}
                    </p>
                    <ul className="list-disc pl-6 mb-4 space-y-1">
                        <li>
                            {t('privacy.yourRights.gdpr.item1', {
                                fallback:
                                    'The right to access, update or delete the information we have on you.',
                            })}
                        </li>
                        <li>
                            {t('privacy.yourRights.gdpr.item2', {
                                fallback: 'The right of rectification.',
                            })}
                        </li>
                        <li>
                            {t('privacy.yourRights.gdpr.item3', {
                                fallback: 'The right to object.',
                            })}
                        </li>
                        <li>
                            {t('privacy.yourRights.gdpr.item4', {
                                fallback: 'The right of restriction.',
                            })}
                        </li>
                        <li>
                            {t('privacy.yourRights.gdpr.item5', {
                                fallback: 'The right to data portability.',
                            })}
                        </li>
                        <li>
                            {t('privacy.yourRights.gdpr.item6', {
                                fallback: 'The right to withdraw consent.',
                            })}
                        </li>
                    </ul>
                </section>

                {/* Children's Privacy */}
                <section className="mb-8">
                    <h2 className="text-2xl font-semibold mb-4">
                        {t('privacy.children.title', {
                            fallback: "8. Children's Privacy",
                        })}
                    </h2>
                    <p className="mb-4">
                        {t('privacy.children.p1', {
                            fallback:
                                'Our Service does not address anyone under the age of 13. We do not knowingly collect personally identifiable information from children under 13. If you are a parent or guardian and you are aware that your child has provided us with Personal Data, please contact us. If we become aware that we have collected Personal Data from children without verification of parental consent, we take steps to remove that information from our servers.',
                        })}
                    </p>
                </section>

                {/* Changes to This Privacy Policy */}
                <section className="mb-8">
                    <h2 className="text-2xl font-semibold mb-4">
                        {t('privacy.changes.title', {
                            fallback: '9. Changes to This Privacy Policy',
                        })}
                    </h2>
                    <p className="mb-4">
                        {t('privacy.changes.p1', {
                            fallback:
                                'We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page.',
                        })}
                    </p>
                    <p className="mb-4">
                        {t('privacy.changes.p2', {
                            fallback:
                                'We will let you know via email and/or a prominent notice on our Service, prior to the change becoming effective and update the "Last updated" date at the top of this Privacy Policy.',
                        })}
                    </p>
                    <p className="mb-4">
                        {t('privacy.changes.p3', {
                            fallback:
                                'You are advised to review this Privacy Policy periodically for any changes. Changes to this Privacy Policy are effective when they are posted on this page.',
                        })}
                    </p>
                </section>

                {/* Contact Us */}
                <section className="mb-8">
                    <h2 className="text-2xl font-semibold mb-4">
                        {t('privacy.contact.title', {
                            fallback: '10. Contact Us',
                        })}
                    </h2>
                    <p className="mb-4">
                        {t('privacy.contact.p1', {
                            fallback:
                                'If you have any questions about this Privacy Policy, please contact us:',
                        })}
                    </p>
                    <ul className="list-disc pl-6 mb-4 space-y-1">
                        <li>
                            {t('privacy.contact.item1', {
                                fallback: 'By email: support@urlprompter.com',
                            })}
                        </li>
                    </ul>
                </section>
            </div>
        </div>
    );
}
